import type { PurchasesIntroPrice, PurchasesStoreProduct } from "@revenuecat/purchases-capacitor";

import { formatTemplate } from "@/i18n/formatTemplate";

export type PaywallPlanPricing = {
  price: string;
  monthlyPrice: string | null;
  badge: string | null;
  originalPrice: string | null;
  trialDays: number | null;
};

export type PaywallPricing = {
  yearly: PaywallPlanPricing;
  lifetime: PaywallPlanPricing;
};

export type PaywallPricingTemplates = {
  yearlyBadgeWithTrial: string;
  yearlyBadgeNoTrial: string;
};

/** Historical anchor ratio: €49.99 strikethrough over €29.99 lifetime price. */
export const LIFETIME_ANCHOR_RATIO = 49.99 / 29.99;

type PriceStringLayout = {
  prefix: string;
  suffix: string;
  decimalSeparator: string;
  groupingSeparator: string;
  fractionDigits: number;
};

const CURRENCY_SYMBOL_LOCALE: Record<string, string> = {
  USD: "en-US",
  EUR: "de-DE",
  GBP: "en-GB",
  JPY: "ja-JP",
};

function currencyFractionDigits(currencyCode: string, intlLocale: string): number {
  try {
    const { maximumFractionDigits = 2 } = new Intl.NumberFormat(intlLocale, {
      style: "currency",
      currency: currencyCode,
    }).resolvedOptions();
    return maximumFractionDigits;
  } catch {
    return 2;
  }
}

function parseNumericSeparators(numericPart: string): Pick<
  PriceStringLayout,
  "decimalSeparator" | "groupingSeparator" | "fractionDigits"
> {
  const hasComma = numericPart.includes(",");
  const hasDot = numericPart.includes(".");

  if (hasComma && hasDot) {
    const lastComma = numericPart.lastIndexOf(",");
    const lastDot = numericPart.lastIndexOf(".");
    if (lastComma > lastDot) {
      return {
        groupingSeparator: ".",
        decimalSeparator: ",",
        fractionDigits: numericPart.length - lastComma - 1,
      };
    }
    return {
      groupingSeparator: ",",
      decimalSeparator: ".",
      fractionDigits: numericPart.length - lastDot - 1,
    };
  }

  if (hasComma) {
    const lastComma = numericPart.lastIndexOf(",");
    const afterComma = numericPart.slice(lastComma + 1);
    if (afterComma.length === 2 && /^\d{2}$/.test(afterComma)) {
      return { groupingSeparator: "", decimalSeparator: ",", fractionDigits: 2 };
    }
    return { groupingSeparator: ",", decimalSeparator: "", fractionDigits: 0 };
  }

  if (hasDot) {
    const lastDot = numericPart.lastIndexOf(".");
    const afterDot = numericPart.slice(lastDot + 1);
    if (afterDot.length === 2 && /^\d{2}$/.test(afterDot)) {
      return { groupingSeparator: "", decimalSeparator: ".", fractionDigits: 2 };
    }
    return { groupingSeparator: ".", decimalSeparator: "", fractionDigits: 0 };
  }

  return { groupingSeparator: "", decimalSeparator: "", fractionDigits: 0 };
}

function parsePriceStringLayout(reference: string): PriceStringLayout | null {
  const match = reference.match(/(-?[\d.,\s\u00a0]+)/);
  if (!match?.[1]) return null;

  const numericPart = match[1].replace(/[\s\u00a0]/g, "");
  const separators = parseNumericSeparators(numericPart);

  return {
    prefix: reference.slice(0, match.index),
    suffix: reference.slice(match.index! + match[0].length),
    ...separators,
  };
}

function addGrouping(intPart: string, groupingSeparator: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupingSeparator);
}

function formatAnchorWithLayout(anchor: number, layout: PriceStringLayout): string {
  const { fractionDigits, decimalSeparator, groupingSeparator, prefix, suffix } = layout;

  let numeric: string;
  if (fractionDigits === 0) {
    numeric = Math.round(anchor).toString();
  } else {
    const [intPart, decPart] = anchor.toFixed(fractionDigits).split(".");
    const groupedInt = groupingSeparator ? addGrouping(intPart, groupingSeparator) : intPart;
    numeric = `${groupedInt}${decimalSeparator}${decPart}`;
  }

  if (groupingSeparator && fractionDigits === 0) {
    numeric = addGrouping(numeric, groupingSeparator);
  }

  return `${prefix}${numeric}${suffix}`;
}

function formatAnchorWithSymbolLocale(
  anchor: number,
  currencyCode: string,
  fractionDigits: number,
): string | null {
  const locale = CURRENCY_SYMBOL_LOCALE[currencyCode] ?? "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "symbol",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(anchor);
  } catch {
    return null;
  }
}

/** Computes a crossed-out anchor price from the store lifetime price. */
export function computeLifetimeAnchorPrice(
  price: number,
  currencyCode: string,
  intlLocale: string,
  referencePriceString?: string | null,
): string | null {
  if (!price || !currencyCode) return null;

  const rawAnchor = price * LIFETIME_ANCHOR_RATIO;
  const fractionDigits = currencyFractionDigits(currencyCode, intlLocale);

  let anchor: number;
  if (fractionDigits === 0) {
    // Zero-decimal currencies (JPY, KRW, …): psychological "ends in 99" (e.g. ¥4,999).
    anchor = Math.floor(rawAnchor / 100) * 100 + 99;
    if (anchor <= price) {
      anchor = Math.floor(price / 100) * 100 + 199;
    }
  } else {
    anchor = Math.floor(rawAnchor) + 0.99;
    if (anchor <= price) {
      anchor = Math.floor(price) + 10.99;
    }
  }

  if (referencePriceString) {
    const layout = parsePriceStringLayout(referencePriceString);
    if (layout) {
      return formatAnchorWithLayout(anchor, layout);
    }
  }

  return formatAnchorWithSymbolLocale(anchor, currencyCode, fractionDigits);
}

/** Converts a free-trial intro offer into a day count for paywall copy. */
export function introTrialDays(intro: PurchasesIntroPrice | null | undefined): number | null {
  if (!intro || intro.price !== 0) return null;

  const units = intro.periodNumberOfUnits;
  if (!units || units <= 0) return null;

  switch (intro.periodUnit) {
    case "DAY":
      return units;
    case "WEEK":
      return units * 7;
    case "MONTH":
      return units * 30;
    case "YEAR":
      return units * 365;
    default:
      return null;
  }
}

function formatMonthlyEquivalent(
  price: number,
  currencyCode: string,
  intlLocale: string,
  pricePerMonthString: string | null | undefined,
): string | null {
  if (pricePerMonthString) return pricePerMonthString;
  if (!price || !currencyCode) return null;

  try {
    return new Intl.NumberFormat(intlLocale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price / 12);
  } catch {
    return null;
  }
}

function planPricingFromProduct(
  product: PurchasesStoreProduct,
  intlLocale: string,
  templates: PaywallPricingTemplates,
  includeTrialBadge: boolean,
): PaywallPlanPricing {
  const trialDays = introTrialDays(product.introPrice);
  const monthlyPrice = formatMonthlyEquivalent(
    product.price,
    product.currencyCode,
    intlLocale,
    product.pricePerMonthString,
  );

  const badge =
    includeTrialBadge && trialDays
      ? formatTemplate(templates.yearlyBadgeWithTrial, {
          trialDays,
          price: product.priceString,
        })
      : includeTrialBadge
        ? formatTemplate(templates.yearlyBadgeNoTrial, { price: product.priceString })
        : null;

  return {
    price: product.priceString,
    monthlyPrice,
    badge,
    originalPrice: null,
    trialDays,
  };
}

export function buildPaywallPricing(
  yearlyProduct: PurchasesStoreProduct,
  lifetimeProduct: PurchasesStoreProduct,
  intlLocale: string,
  templates: PaywallPricingTemplates,
): PaywallPricing {
  // Lifetime is a one-time (non-subscription) product: no intro/trial and no
  // monthly equivalent, so it never gets the trial badge.
  const yearly = planPricingFromProduct(yearlyProduct, intlLocale, templates, true);
  const lifetime = {
    ...planPricingFromProduct(lifetimeProduct, intlLocale, templates, false),
    originalPrice: computeLifetimeAnchorPrice(
      lifetimeProduct.price,
      lifetimeProduct.currencyCode,
      intlLocale,
      lifetimeProduct.priceString,
    ),
  };

  return { yearly, lifetime };
}
